<script setup>
import { computed, ref } from 'vue';
import { useAlert } from 'dashboard/composables';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import KraftchatAIAPI from 'dashboard/api/kraftchatAI';

const props = defineProps({
  chat: {
    type: Object,
    required: true,
  },
});

// Kraftchat AI copy is deliberately kept as plain PT-BR strings here,
// matching the rest of the Kraftchat AI surface (see
// kraftchatAIKnowledgeConfig.js / kraftchatAIRulesConfig.js) rather than
// routing through en.json like the surrounding native Chatwoot UI.
const content = {
  activeLabel: 'IA ativa',
  pausedLabel: 'IA pausada',
  activeHelperText:
    'O assistente pode responder às próximas mensagens desta conversa.',
  pausedHelperText: 'As próximas mensagens aguardam atendimento humano.',
  toggleError: 'Não foi possível atualizar o controle da IA.',
};

const isSaving = ref(false);

// Single source of truth: conversation.custom_attributes.kraftchat_ai.handoff.
// No local ai_enabled/copilot_enabled boolean - the switch is a controlled
// component that always reflects the current Conversation from the store,
// including updates that arrive from other sources (the AI itself, the
// Resolve button, HandoffTimeoutService, another agent) via the existing
// realtime conversation-update pipeline.
const isHandoffActive = computed(
  () => !!props.chat.custom_attributes?.kraftchat_ai?.handoff
);
const isAiActive = computed(() => !isHandoffActive.value);

const label = computed(() =>
  isAiActive.value ? content.activeLabel : content.pausedLabel
);
const helperText = computed(() =>
  isAiActive.value ? content.activeHelperText : content.pausedHelperText
);

const handleToggle = async nextActive => {
  if (isSaving.value) return;

  isSaving.value = true;
  try {
    await KraftchatAIAPI.updateConversationAiControl(props.chat.id, nextActive);
    // No optimistic local commit - the backend broadcasts the updated
    // conversation over the existing realtime channel, which the
    // conversations store already listens to.
  } catch (error) {
    useAlert(error?.message || content.toggleError);
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <div
    v-tooltip="helperText"
    class="flex items-center gap-1.5 px-2 h-7 rounded-lg border border-n-weak bg-n-solid-1 flex-shrink-0"
  >
    <span class="text-xs font-medium text-n-slate-11 whitespace-nowrap">
      {{ label }}
    </span>
    <Switch
      :model-value="isAiActive"
      :disabled="isSaving"
      @update:model-value="handleToggle"
    />
  </div>
</template>
